import React, { useState, useEffect } from 'react'
import { Modal, Select, Button, Input } from 'antd'
import moment from 'moment'
import MultiCauseCodePropmt from '../../card-data/MultiCauseCode'
import { DERATE_TYPES, OUTAGE_TYPES } from './eventTypeRules'

const { Option } = Select

const MoveEventModal = ({
  visible,
  onClose,
  event,
  eventTypes,
  causeCodeList,
  onMove,
  moving,
}) => {
  const [newEventType, setNewEventType] = useState(null)
  const [causeCodeInput, setCauseCodeInput] = useState('')
  const [selectedCauseCode, setSelectedCauseCode] = useState(null)
  const [causeCodeSearchVisible, setCauseCodeSearchVisible] = useState(false)
  const [causeCodeKeyValues, setCauseCodeKeyValues] = useState(null)
  const [causeCodeValues, setCauseCodeValues] = useState(null)

  useEffect(() => {
    if (!visible) {
      setNewEventType(null)
      setCauseCodeInput('')
      setSelectedCauseCode(null)
      setCauseCodeSearchVisible(false)
      setCauseCodeKeyValues(null)
      setCauseCodeValues(null)
    }
  }, [visible])

  const onCauseCodeDialogClose = (keys, values) => {
    setCauseCodeSearchVisible(false)
    setCauseCodeKeyValues(keys)
    setCauseCodeValues(values)
    if (values && values.length > 0) {
      const cc = values[0]
      setSelectedCauseCode(cc)
      setCauseCodeInput(String(cc.causeCode))
    }
  }

  const onCauseCodeTreeCancel = keys => {
    setCauseCodeSearchVisible(false)
    setCauseCodeKeyValues(keys)
  }

  // Description lookup from typed input
  const causeCodeDesc = (() => {
    if (!causeCodeInput || !causeCodeList) return null
    const num = parseInt(
      String(causeCodeInput)
        .split(' - ')[0]
        .trim(),
      10
    )
    if (isNaN(num)) return null
    const match = causeCodeList.find(cc => Number(cc.causeCode) === num)
    return match ? match.description : null
  })()

  const resolvedCauseCode = selectedCauseCode
    ? selectedCauseCode.causeCode
    : causeCodeInput
    ? parseInt(
        String(causeCodeInput)
          .split(' - ')[0]
          .trim(),
        10
      )
    : null

  const causeCodeValid =
    !!causeCodeInput &&
    (!(causeCodeList && causeCodeList.length > 0) || causeCodeDesc != null)

  const canMove =
    newEventType != null &&
    causeCodeValid &&
    !moving &&
    (newEventType !== (event && event.eventType) ||
      Number(resolvedCauseCode) !== Number(event && event.causeCode))

  const handleMove = () => {
    if (!canMove) return
    onMove(newEventType, resolvedCauseCode)
  }

  const ro = (label, value) => (
    <div style={{ display: 'flex', marginBottom: 8 }}>
      <span style={{ width: 110, color: '#555', fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500 }}>{value ?? '—'}</span>
    </div>
  )

  return (
    <>
      <Modal
        title="Move Event"
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={420}
        destroyOnClose
        maskClosable={false}
      >
        {event && (
          <>
            <div
              style={{
                background: '#fafafa',
                border: '1px solid #e8e8e8',
                borderRadius: 4,
                padding: '10px 14px',
                marginBottom: 16,
              }}
            >
              {ro(
                'Start Time',
                event.eventStart
                  ? moment(event.eventStart).format('HH:mm')
                  : null
              )}
              {ro(
                'AvailCap',
                event.availCap != null
                  ? Number(event.availCap).toFixed(1)
                  : null
              )}
              {ro('Event Type', event.eventType)}
              {ro('Cause Code', event.causeCode)}
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                New Event Type
              </div>
              <Select
                value={newEventType}
                onChange={setNewEventType}
                placeholder="Select event type"
                showSearch
                style={{ width: '100%' }}
                filterOption={(input, opt) =>
                  opt.props.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {(eventTypes || [])
                  .filter(
                    e =>
                      DERATE_TYPES.has(e.eventType) ||
                      OUTAGE_TYPES.has(e.eventType)
                  )
                  .map(e => (
                    <Option key={e.eventType} value={e.eventType}>
                      {e.eventType} - {e.description}
                    </Option>
                  ))}
              </Select>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                New Cause Code
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  value={causeCodeInput}
                  placeholder="Enter or select cause code"
                  onChange={e => {
                    setCauseCodeInput(e.target.value)
                    setSelectedCauseCode(null)
                  }}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  onClick={() => setCauseCodeSearchVisible(true)}
                >
                  Select
                </Button>
              </div>
              {causeCodeInput && causeCodeDesc && (
                <div style={{ marginTop: 4, color: '#1890ff', fontSize: 12 }}>
                  {causeCodeDesc}
                </div>
              )}
              {causeCodeInput &&
                !causeCodeDesc &&
                causeCodeList &&
                causeCodeList.length > 0 && (
                  <div style={{ marginTop: 4, color: '#f5222d', fontSize: 12 }}>
                    Cause code not found
                  </div>
                )}
            </div>

            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
            >
              <Button onClick={onClose} disabled={moving}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleMove}
                disabled={!canMove}
                loading={moving}
              >
                Move
              </Button>
            </div>
          </>
        )}
      </Modal>

      {causeCodeSearchVisible && (
        <MultiCauseCodePropmt
          key="move-event-causecode-prompt"
          visibleDialog={causeCodeSearchVisible}
          onDialogClose={onCauseCodeDialogClose}
          onCancelAction={onCauseCodeTreeCancel}
          selectedKeys={causeCodeKeyValues}
          selectedValues={causeCodeValues}
          causeCodeList={causeCodeList}
          causeCodeValue={
            selectedCauseCode ? [selectedCauseCode.causeCode] : []
          }
          unitCodes={[]}
          multiple={false}
          checkable={false}
          zIndex={1200}
          selectable
        />
      )}
    </>
  )
}

export default MoveEventModal
